import { SPAN_OVERLOAD_THRESHOLD } from './orgAnalyticLensRules';
import React from 'react';
import { create } from 'zustand';
import {
  Eye,
  Users,
  DollarSign,
  Clock,
  Building2,
  BriefcaseBusiness,
  SlidersHorizontal,
  PencilLine,
  Star,
  Loader2,
} from 'lucide-react';
import {
  ViewsGallery,
  useRecipeUrlState,
  applyRecipeLenses,
  clearRecipeLenses,
  type IView,
  type IViewGroup,
  type RecipeLensSpec,
} from '../../../core/assistant';
import { getNodeMutationManager } from '../../../core/commands/mutations/NodeMutationManager';
import { getCommandManager } from '../../../hooks/useCommandManager';
import type { DiagramSettings } from '../../../hooks/useDiagramSettings';
import { useDiagramStore } from '../../../stores/diagramStore';
import { DRAW_ORG_CHART_VIEW_ID, WORKLOAD_CONTEXT_VIEW_ID } from './orgChartAssistantShared';
import { useOrgChartViewStore } from '../../../stores/orgchartViewStore';
import { LayoutOrgChartCommand } from '../commands/LayoutOrgChartCommand';
import {
  computeFullTreeLayout,
  isVisibleOrgLayoutConnector,
  ORG_DISPLAY_RULE_LABEL_CLEARANCE,
} from '../services/graphAnalysis';
import { applyOrgChartCollapse } from '../services/orgChartCollapseProjection';
import {
  SUCCESSION_LENS_ADAPTER,
  SUCCESSION_LENS_ID,
  isSuccessionLensActive,
} from './successionLens';
import { useViewRulesStore } from '../../../core/assistant/stores/viewRulesStore';

/**
 * OrgChartViews — named, one-click composite "views" for org charts, built
 * on the shared View primitive (the Blueprint "View Box" / "Views gallery"
 * model). Each view answers a concern by composing several view-rule lenses
 * at once, instead of making the user assemble them by hand.
 *
 * Self-contained: apply/clear orchestrate the global viewRulesStore, so the
 * views can drop into the View Box hero and the Views gallery alike.
 * Deep-linkable via ?review=<id>.
 */

interface OrgChartRecipesProps {
  readonly workloadRelationshipCount?: number;
  readonly onViewSelected?: () => void;
  readonly onCustomView?: () => void;
  readonly activeRecipeId?: string | null;
  readonly onSelectRecipe?: (id: string | null) => void;
}

export const DEFAULT_ORG_CHART_VIEW_ID = 'default-view';
export const CUSTOM_ORG_CHART_VIEW_ID = 'custom-view';
export const SUCCESSION_PLANNING_VIEW_ID = 'succession-planning';

/** Below-card labels need a bit more vertical room in leaf stacks. */
const LABEL_VIEW_MIN_VERTICAL_SPACING = 150;

type OrgLabelViewApplyState = {
  applyingId: string | null;
  setApplyingId: (id: string | null) => void;
};

export const useOrgLabelViewApplyStore = create<OrgLabelViewApplyState>((set) => ({
  applyingId: null,
  setApplyingId: (applyingId) => set({ applyingId }),
}));

export const OrgLabelViewApplyingIcon: React.FC<{ className?: string }> = ({ className }) => (
  <Loader2 className={className ? `${className} animate-spin` : 'size-3.5 animate-spin'} />
);

/** Spacing in effect before a label view bumped it; restored when leaving those views. */
let spacingBeforeLabelViewBump: number | null = null;
/** Cancels restore when switching tenure ↔ footprint in the same turn. */
let restoreLabelSpacingPending = false;

const waitAnimationFrames = (count: number): Promise<void> =>
  new Promise((resolve) => {
    if (typeof window === 'undefined' || count <= 0) {
      resolve();
      return;
    }
    let remaining = count;
    const tick = () => {
      remaining -= 1;
      if (remaining <= 0) resolve();
      else window.requestAnimationFrame(tick);
    };
    window.requestAnimationFrame(tick);
  });

const readDiagramSettings = (): DiagramSettings => {
  const doc = getNodeMutationManager().getDoc();
  if (!doc) return {};
  const raw = doc.getMap('diagramSettings').get('settings');
  if (typeof raw !== 'string') return {};
  try {
    return JSON.parse(raw) as DiagramSettings;
  } catch {
    return {};
  }
};

const writeVerticalSpacing = (verticalSpacing: number): void => {
  const doc = getNodeMutationManager().getDoc();
  if (!doc) return;
  const settings = readDiagramSettings();
  doc.getMap('diagramSettings').set(
    'settings',
    JSON.stringify({
      ...settings,
      orgchart: {
        ...settings.orgchart,
        verticalSpacing,
      },
    }),
  );
  useOrgChartViewStore.getState().setVerticalSpacing(verticalSpacing);
};

const reflowOrgChartAtVerticalSpacing = async (verticalSpacing: number): Promise<void> => {
  const settings = readDiagramSettings();
  const { nodes, edges } = useDiagramStore.getState();
  const collapsedNodeIds = useOrgChartViewStore.getState().collapsedNodeIds;
  const collapseProjection = applyOrgChartCollapse(nodes, edges, collapsedNodeIds);
  const layoutEdges = collapseProjection.edges.filter((edge) => {
    const edgeType = edge.type as unknown as string;
    return (
      (edgeType === 'reporting'
        || edgeType === 'dotted'
        || edgeType === 'matrix'
        || edgeType === 'team'
        || edgeType === 'occupies')
      && isVisibleOrgLayoutConnector(edge)
    );
  });
  const positions = computeFullTreeLayout(collapseProjection.nodes, collapseProjection.edges, {
    nodeSpacing: settings.orgchart?.nodeSpacing,
    verticalSpacing,
    displayRuleLabelClearance:
      verticalSpacing >= LABEL_VIEW_MIN_VERTICAL_SPACING ? ORG_DISPLAY_RULE_LABEL_CLEARANCE : 0,
  });
  if (positions.size === 0) return;

  await getCommandManager().execute(
    new LayoutOrgChartCommand({
      positions,
      edges: layoutEdges,
      nodes: collapseProjection.nodes,
      skipHistory: true,
    }),
  );
};

/** Widen spacing and reflow first. Returns true when a reflow ran. */
const prepareSpacingForLabelView = async (): Promise<boolean> => {
  const settings = readDiagramSettings();
  const current = settings.orgchart?.verticalSpacing ?? 120;
  if (current >= LABEL_VIEW_MIN_VERTICAL_SPACING) return false;

  if (spacingBeforeLabelViewBump === null) {
    spacingBeforeLabelViewBump = current;
  }
  writeVerticalSpacing(LABEL_VIEW_MIN_VERTICAL_SPACING);
  await reflowOrgChartAtVerticalSpacing(LABEL_VIEW_MIN_VERTICAL_SPACING);
  return true;
};

const restoreLabelViewSpacingIfNeeded = async (): Promise<void> => {
  if (spacingBeforeLabelViewBump === null) return;
  const previous = spacingBeforeLabelViewBump;
  spacingBeforeLabelViewBump = null;
  writeVerticalSpacing(previous);
  await reflowOrgChartAtVerticalSpacing(previous);
};

/** Spacing first (with spinner while reflowing), then labels/heatmap. */
const applyLabelView = (id: string, specs: RecipeLensSpec[]): void => {
  restoreLabelSpacingPending = false;
  void (async () => {
    const settings = readDiagramSettings();
    const needsSpacingBump = (settings.orgchart?.verticalSpacing ?? 120) < LABEL_VIEW_MIN_VERTICAL_SPACING;

    if (!needsSpacingBump) {
      applyRecipeLenses(specs);
      return;
    }

    useOrgLabelViewApplyStore.getState().setApplyingId(id);
    try {
      await prepareSpacingForLabelView();
      await waitAnimationFrames(2);
      applyRecipeLenses(specs);
    } finally {
      useOrgLabelViewApplyStore.getState().setApplyingId(null);
    }
  })();
};

const clearLabelView = (): void => {
  clearRecipeLenses();
  restoreLabelSpacingPending = true;
  queueMicrotask(() => {
    if (!restoreLabelSpacingPending) return;
    restoreLabelSpacingPending = false;
    void restoreLabelViewSpacingIfNeeded();
  });
};

const labelView = (
  id: string,
  title: string,
  description: string,
  icon: IView['icon'],
  tone: IView['tone'],
  specs: RecipeLensSpec[],
): IView => ({
  id,
  title,
  description,
  icon,
  tone,
  group: 'Reviews',
  apply: () => applyLabelView(id, specs),
  clear: clearLabelView,
});

const defaultOrgChartView = (): IView => ({
  id: DEFAULT_ORG_CHART_VIEW_ID,
  title: 'Default view',
  description: 'Full org chart with no heatmap, extra labels, or view overlay applied.',
  icon: Eye,
  tone: 'slate',
  group: 'Views',
  apply: clearRecipeLenses,
  clear: clearRecipeLenses,
});

const customOrgChartView = (): IView => ({
  id: CUSTOM_ORG_CHART_VIEW_ID,
  title: 'Custom view',
  description: 'Build a view with filters, color, labels, heatmaps, and display controls.',
  icon: SlidersHorizontal,
  tone: 'slate',
  group: 'Views',
  apply: () => undefined,
});

const drawOrgChartView = (): IView => ({
  id: DRAW_ORG_CHART_VIEW_ID,
  title: 'Manual drawing tools',
  description: 'Build an editable chart by hand. For linked charts, use Chart layout to arrange cards.',
  icon: PencilLine,
  tone: 'blue',
  group: 'Views',
  apply: () => {
    clearRecipeLenses();
    useOrgChartViewStore.getState().setAutoLayoutEnabled(false);
  },
  clear: clearRecipeLenses,
});

const detectedWorkloadView = (
  workloadRelationshipCount: number,
): IView => ({
  id: WORKLOAD_CONTEXT_VIEW_ID,
  title: 'Workload & Commitments',
  description: 'Heatmap existing people by current or next scheduled source-backed work.',
  icon: BriefcaseBusiness,
  tone: 'blue',
  group: 'Reviews',
  badge: (
    <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[9px] font-bold uppercase text-blue-700">
      {workloadRelationshipCount}
    </span>
  ),
  apply: () => applyRecipeLenses([
    {
      kind: 'heatmap',
      name: 'Workload Load',
      field: 'workloadDisplayAllocationPercent',
      minColor: '#dcfce7',
      maxColor: '#dc2626',
      minValue: 0,
      maxValue: 120,
    },
    {
      kind: 'label',
      name: 'Workload Summary',
      field: 'workloadCandidateSummary',
      labelTemplate: '{{workloadCandidateSummary}}',
    },
  ]),
  clear: clearRecipeLenses,
});

const successionPlanningView = (): IView => ({
  id: SUCCESSION_PLANNING_VIEW_ID,
  title: 'Succession Planning',
  description: 'Colour by succession risk, flag key positions, and reveal successor readiness for the current selection.',
  icon: Star,
  tone: 'amber',
  group: 'Reviews',
  entitlementKey: 'atlas.view.analytic',
  apply: () => {
    clearRecipeLenses();
    SUCCESSION_LENS_ADAPTER.apply(SUCCESSION_LENS_ID);
  },
  clear: () => SUCCESSION_LENS_ADAPTER.clear(SUCCESSION_LENS_ID),
  // Lets the deep-link plumbing re-assert the lens after the view-rules store
  // re-initializes (doc arrival replaces rules wholesale) without re-writing
  // rules that survived or were already persisted in the doc.
  isApplied: () => isSuccessionLensActive(useViewRulesStore.getState().rules),
});

export const ORG_CHART_VIEWS: readonly IView[] = [
  labelView(
    'span-overload',
    'Span Overload',
    'Direct-report counts in blue; red flags more than 8 direct reports. This is a review threshold, not a workload assessment.',
    Users,
    'red',
    [
      { kind: 'heatmap', field: 'directReports', minColor: '#dbeafe', maxColor: '#1e40af' },
      { kind: 'label', field: 'directReports' },
      {
        kind: 'highlight', field: 'directReports', name: 'Over 8 direct reports',
        conditions: [{ field: 'directReports', fieldType: 'number', operator: 'greater_than', value: SPAN_OVERLOAD_THRESHOLD }],
        display: { scopeMode: 'custom', colors: { backgroundColor: '#fee2e2', textColor: '#991b1b' } },
      },
    ],
  ),
  labelView(
    'comp-review',
    'Compensation Review',
    'Colour by band with salary labelled — spot comp outliers within each grade.',
    DollarSign,
    'emerald',
    [
      { kind: 'colorBy', field: 'band' },
      { kind: 'label', field: 'salary' },
    ],
  ),
  successionPlanningView(),
  labelView(
    'tenure-retention',
    'Tenure & Retention',
    'Heatmap by tenure with management level labelled — surface long-tenured ICs and new managers.',
    Clock,
    'amber',
    [
      { kind: 'heatmap', field: 'tenure', minColor: '#dbeafe', maxColor: '#1e40af' },
      { kind: 'label', field: 'managementLevel' },
    ],
  ),
  labelView(
    'org-footprint',
    'Org Footprint',
    'Colour by department with location labelled — see how teams spread across sites.',
    Building2,
    'blue',
    [
      { kind: 'colorBy', field: 'department' },
      { kind: 'label', field: 'location' },
    ],
  ),
];

export const ORG_CHART_VIEW_GROUPS: readonly IViewGroup[] = [
  { views: ORG_CHART_VIEWS },
];

export function buildOrgChartViews({
  workloadRelationshipCount = 0,
}: OrgChartRecipesProps = {}): readonly IView[] {
  const defaultView = defaultOrgChartView();
  const drawView = drawOrgChartView();

  if (workloadRelationshipCount <= 0) {
    return [defaultView, drawView, ...ORG_CHART_VIEWS];
  }

  return [
    defaultView,
    drawView,
    detectedWorkloadView(workloadRelationshipCount),
    ...ORG_CHART_VIEWS,
  ];
}

export function buildOrgChartPickerViews({
  workloadRelationshipCount = 0,
}: OrgChartRecipesProps = {}): readonly IView[] {
  return [
    ...buildOrgChartViews({ workloadRelationshipCount }),
    customOrgChartView(),
  ];
}

/** Active org-chart view, deep-linked via ?review=. Shared by the View Box
 *  hero and the Views gallery so they always agree. */
export const useOrgChartViewState = (
  views: readonly IView[] = ORG_CHART_VIEWS,
) => {
  // The view-rules store re-initializes when the collaborative doc arrives,
  // replacing rules wholesale — which raced (and often erased) the deep-link
  // apply that ran on mount. The store's doc identity is the reset signal.
  const rulesDoc = useViewRulesStore((state) => state.ydoc);
  return useRecipeUrlState(views, { param: 'review', reapplyKey: rulesDoc });
};

export const OrgChartRecipes: React.FC<OrgChartRecipesProps> = ({
  workloadRelationshipCount = 0,
  onViewSelected,
  onCustomView,
  activeRecipeId: controlledActiveRecipeId,
  onSelectRecipe,
}) => {
  const applyingId = useOrgLabelViewApplyStore((state) => state.applyingId);
  const views = React.useMemo(
    () => buildOrgChartPickerViews({ workloadRelationshipCount }),
    [workloadRelationshipCount],
  );
  const displayViews = React.useMemo(
    () => views.map((item) => (
      item.id === applyingId
        ? {
          ...item,
          badge: <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />,
        }
        : item
    )),
    [applyingId, views],
  );
  const groups = React.useMemo<readonly IViewGroup[]>(
    () => [{ views: displayViews }],
    [displayViews],
  );
  const recipeState = useOrgChartViewState(views);
  const activeRecipeId =
    controlledActiveRecipeId === undefined
      ? recipeState.activeRecipeId
      : controlledActiveRecipeId;
  const selectRecipe = onSelectRecipe ?? recipeState.selectRecipe;
  const activeGalleryViewId = activeRecipeId ?? DEFAULT_ORG_CHART_VIEW_ID;
  const [shouldReturnAfterSelection, setShouldReturnAfterSelection] = React.useState(false);

  React.useEffect(() => {
    if (!shouldReturnAfterSelection) return;
    if (applyingId) return;
    setShouldReturnAfterSelection(false);
    onViewSelected?.();
  }, [activeRecipeId, applyingId, onViewSelected, shouldReturnAfterSelection]);

  return (
    <ViewsGallery
      groups={groups}
      activeViewId={activeGalleryViewId}
      onSelect={(v) => {
        if (applyingId) return;
        if (v.id === CUSTOM_ORG_CHART_VIEW_ID) {
          onCustomView?.();
          return;
        }
        selectRecipe(v.id === DEFAULT_ORG_CHART_VIEW_ID ? null : v.id);
        setShouldReturnAfterSelection(true);
      }}
      allowDeselect
      onClear={() => {
        if (applyingId) return;
        selectRecipe(null);
        setShouldReturnAfterSelection(true);
      }}
    />
  );
};
